import { Close, Star } from '@mui/icons-material';
import { Grid, Box, Typography, Rating, GridProps } from '@mui/material';
import ReviewFormModal, {
  ReviewFormModalValues,
} from 'components/common/ReviewFormModal';
import {
  useAddSparkRating,
  useSparksByID,
  useUpdateSparkRating,
} from 'hooks/spark';
import React, { useState, useEffect } from 'react';
import { useUserStore } from 'stores';

type OutputRatingPanelProps = {
  sparkID: string | undefined;
  suggestion?: string;
  handleHide?: () => void;
  panelHidden?: boolean;
  ShowCloseIcon?: boolean;
  containerStyles?: GridProps['sx'];
};

export default function OutputRatingPanel({
  sparkID,
  panelHidden,
  handleHide,
  ShowCloseIcon = false,
  suggestion = 'Leave a rating for the community',
  containerStyles = {},
}: OutputRatingPanelProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [currentRating, setCurrentRating] = useState<number | null>(null);
  const currentUserID = useUserStore(
    React.useCallback((state) => state.currentUserID, [])
  );

  const { joinedSpark } = useSparksByID(sparkID);
  const { ratings } = joinedSpark?.meta.user || {};
  const userRating = ratings?.find(
    (rating) => rating.sparkID === joinedSpark?.id
  );

  const { mutateAsync: updateSparkRating, isLoading: isUpdatingSparkRating } =
    useUpdateSparkRating(userRating?.id ? `${userRating?.id}` : undefined);
  const { mutateAsync: addSparkRating, isLoading: isAddingSparkRating } =
    useAddSparkRating();

  const averageRating = React.useMemo(() => {
    if (!ratings?.length) {
      return 0;
    }
    return ratings.reduce((acc, curr) => acc + curr.rating, 0) / ratings.length;
  }, [ratings]);

  const handleChange = (value: number | null) => {
    if (value !== null) {
      setCurrentRating(value);
      setModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleSave = async (values: ReviewFormModalValues) => {
    await handleAddRating(values);
    setModalOpen(false);
  };

  const handleAddRating = async (values: ReviewFormModalValues) => {
    if (!!userRating) {
      await updateSparkRating({
        rating: currentRating || 0,
        sparkID,
        userID: currentUserID,
        comment: values?.comment,
      });
    } else {
      await addSparkRating({
        rating: currentRating || 0,
        sparkID,
        userID: currentUserID,
        comment: values?.comment,
      });
    }
  };

  const isMutating = isAddingSparkRating || isUpdatingSparkRating;
  return (
    <Grid container item display={panelHidden ? 'none' : 'block'}>
      <Grid
        item
        md={12}
        container
        justifyContent='space-between'
        bgcolor={(theme) => theme.palette.primary.light}
        border={(theme) => `1px solid ${theme.palette.primary.main}`}
        padding={2}
        alignItems='center'
        borderRadius='10px'
        sx={{
          '&:hover': {
            '& .close': {
              opacity: '1 !important',
            },
          },
          ...containerStyles,
        }}
      >
        <Box>
          <Typography fontSize={'1rem'} fontFamily={'Mont-SemiBold'}>
            What do you think of {joinedSpark?.attributes?.nameforHuman}?
          </Typography>
          <Typography fontSize={'0.8rem'} fontWeight={400}>
            {suggestion}
          </Typography>
        </Box>
        <Box display='flex' gap={1} alignItems={'center'}>
          <Rating
            value={currentRating || 0}
            precision={1}
            max={5}
            onChange={(_, value) => handleChange(value)}
            disabled={isMutating}
            emptyIcon={<Star style={{ color: '#D9D9D9' }} />}
            style={{
              cursor: isMutating ? 'not-allowed' : 'pointer',
            }}
          />
          {ShowCloseIcon && (
            <Box
              className='close'
              sx={{ cursor: 'pointer', opacity: '0' }}
              onClick={handleHide}
              marginTop={'8px'}
            >
              <Close />
            </Box>
          )}
        </Box>
      </Grid>
      <ReviewFormModal
        open={modalOpen}
        onClose={handleCloseModal}
        rating={{ rating: currentRating, comment: '' } as any}
        allowedToReview={true}
        onSave={handleSave}
        logoURL={joinedSpark?.attributes?.iconURL || ''}
        resourceName={joinedSpark?.attributes?.nameforHuman || ''}
        setCurrentRating={setCurrentRating}
      />
    </Grid>
  );
}
